import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { GetFanClubStatusResponse, ShowSummary } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import {
  buildShowPromoSlides,
  type ShowPromoAction,
  type ShowPromoSlide,
} from './show-promo-slides.js';
import './ShowPromoOverlay.css';

const ROTATE_MS = 6_500;
/** Main Stage keeps the stream clear, then nags after this interval. */
const MAIN_STAGE_NAG_MS = 15 * 60 * 1000;

export interface ShowPromoOverlayProps {
  show: ShowSummary;
  user: { id: string } | null;
  fanClub: GetFanClubStatusResponse | null;
  modelUsername: string;
  isJoining?: boolean;
  onAction: (action: ShowPromoAction) => void;
}

export function ShowPromoOverlay({
  show,
  user,
  fanClub,
  modelUsername,
  isJoining = false,
  onAction,
}: ShowPromoOverlayProps): ReactElement | null {
  const isNagMode =
    show.type === 'MAIN_STAGE' && show.status !== 'PAUSED' && show.status !== 'AWAY';
  const slides = buildShowPromoSlides({ show, user, fanClub, modelUsername });
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [nagReady, setNagReady] = useState(!isNagMode);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setIndex(0);
    setDismissed(false);
    if (!isNagMode) {
      setNagReady(true);
      return;
    }

    setNagReady(false);
    const timerId = window.setTimeout(() => {
      setNagReady(true);
    }, MAIN_STAGE_NAG_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [show.id, show.status, isNagMode, user?.id]);

  useEffect(() => {
    setIndex(0);
  }, [show.type, show.status, show.isViewer]);

  useEffect(() => {
    if (slides.length <= 1 || paused || (isNagMode && (!nagReady || dismissed))) {
      return;
    }

    const timerId = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, ROTATE_MS);

    return () => {
      window.clearInterval(timerId);
    };
  }, [dismissed, isNagMode, nagReady, paused, slides.length]);

  if (slides.length === 0) {
    return null;
  }

  if (isNagMode && (!nagReady || dismissed)) {
    return null;
  }

  const safeIndex = index % slides.length;
  const slide: ShowPromoSlide = slides[safeIndex]!;

  return (
    <aside
      className={isNagMode ? 'show-promo show-promo--nag' : 'show-promo'}
      aria-label={t('common.show.promoRotateHint')}
      onMouseEnter={() => {
        setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
      onFocusCapture={() => {
        setPaused(true);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      <div className="show-promo__card" key={slide.id}>
        {isNagMode ? (
          <button
            type="button"
            className="show-promo__dismiss"
            aria-label={t('common.show.promoDismiss')}
            onClick={() => {
              setDismissed(true);
            }}
          >
            ×
          </button>
        ) : null}
        <p className="show-promo__eyebrow">{slide.eyebrow}</p>
        <h2 className="show-promo__title">{slide.title}</h2>
        <p className="show-promo__body">{slide.body}</p>
        <div className="show-promo__footer">
          <Button
            label={slide.ctaLabel}
            variant="primary"
            disabled={isJoining && show.type !== 'EXCLUSIVE'}
            onClick={() => {
              onAction(slide.action);
            }}
          />
          {slides.length > 1 ? (
            <div className="show-promo__dots" role="tablist" aria-label={t('common.show.promoRotateHint')}>
              {slides.map((item, dotIndex) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={dotIndex === safeIndex}
                  className={
                    dotIndex === safeIndex
                      ? 'show-promo__dot show-promo__dot--active'
                      : 'show-promo__dot'
                  }
                  onClick={() => {
                    setIndex(dotIndex);
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
